/**
 * RTWO Cryptography & Identity Engine — Repository Bootstrap Service
 */

import {
  CRYPTO_REPOSITORY_DEFINITIONS,
  CRYPTO_SERVICE_IDS,
  type CryptoRepositoryAccessStatus,
  type RTWOCryptoRepositoryDescriptor,
} from '../domain-models';
import type { IRepositoryBootstrapService, RepositoryBootstrapResult } from '../service-interfaces';
import type { IdentityBackendRegistry, KeyProviderRegistry } from '../registry/provider-registries';
import type { CryptoRepositories } from '../repositories/crypto-repositories';
import { CryptoEventTypes } from '../events/crypto-event-bus';
import { BaseCryptoService, type CryptoServiceContext } from './base-crypto-service';
import { isOperationalKeyProvider } from '../providers/crypto-provider-factory';

export class RepositoryBootstrapService
  extends BaseCryptoService
  implements IRepositoryBootstrapService
{
  readonly serviceId = CRYPTO_SERVICE_IDS.REPOSITORY_BOOTSTRAP;
  readonly serviceName = 'Repository Bootstrap Service';
  readonly description =
    'Bootstraps in-memory repositories when key provider and identity backend are ready.';
  readonly layer = 'R6' as const;
  readonly integrations = ['Key Provider', 'Identity Backend', 'Crypto Repositories'];
  readonly requiresKeyProvider = false;
  readonly requiresIdentityBackend = false;

  private repositoryDescriptors: RTWOCryptoRepositoryDescriptor[] =
    CRYPTO_REPOSITORY_DEFINITIONS.map((descriptor) => ({ ...descriptor }));

  constructor(
    context: CryptoServiceContext,
    private readonly keyProviderRegistry: KeyProviderRegistry,
    private readonly identityBackendRegistry: IdentityBackendRegistry,
    private readonly repositories: CryptoRepositories
  ) {
    super(context);
  }

  async initialize(): Promise<void> {
    this.status = 'ready';
  }

  async bootstrapRepositories(): Promise<RepositoryBootstrapResult> {
    const keyReady = this.keyProviderRegistry.getActiveProvider().isReady();
    const identityReady = this.identityBackendRegistry.getActiveBackend().isReady();

    if (!keyReady && !identityReady) {
      const keyConfigured = this.keyProviderRegistry.isConfigured();
      const identityConfigured = this.identityBackendRegistry.isConfigured();

      if (!keyConfigured && !identityConfigured) {
        return {
          success: false,
          repositoryCount: 6,
          accessStatus: 'not_configured',
          error: 'Repository bootstrap requires configured key provider or identity backend',
          source: 'Cryptography Provider — Providers Not Configured',
        };
      }

      const keyProvider = this.keyProviderRegistry.getActiveProvider();
      const operational = isOperationalKeyProvider(keyProvider.providerType);

      return {
        success: false,
        repositoryCount: 6,
        accessStatus: 'not_configured',
        error: operational
          ? `Repository bootstrap requires connected ${keyProvider.providerName}`
          : 'Repository bootstrap requires ready providers — scaffolds cannot bootstrap repositories',
        source: 'Cryptography Provider — Real Cryptographic Implementation Required',
        scaffoldOnly: !operational,
      };
    }

    const bootstrappedAt = Date.now();
    this.repositoryDescriptors = CRYPTO_REPOSITORY_DEFINITIONS.map((descriptor) => ({
      ...descriptor,
      accessStatus: 'read_write' as const,
      recordCount: this.getRecordCount(descriptor.repositoryId),
      lastAccessedAt: bootstrappedAt,
    }));

    this.context.eventBus.emit(CryptoEventTypes.REPOSITORIES_BOOTSTRAPPED, 'crypto-repositories', {
      repositoryCount: 6,
      keyProviderReady: keyReady,
      identityBackendReady: identityReady,
    });

    return {
      success: true,
      repositoryCount: 6,
      accessStatus: 'read_write',
      error: null,
      source: 'Cryptography Provider — Repositories Bootstrapped',
    };
  }

  getRepositoryDescriptors(): RTWOCryptoRepositoryDescriptor[] {
    const keyReady = this.keyProviderRegistry.getActiveProvider().isReady();
    const identityReady = this.identityBackendRegistry.getActiveBackend().isReady();
    const anyReady = keyReady || identityReady;

    return this.repositoryDescriptors.map((descriptor) => ({
      ...descriptor,
      accessStatus: anyReady ? 'read_write' : ('not_configured' as CryptoRepositoryAccessStatus),
      recordCount: anyReady ? this.getRecordCount(descriptor.repositoryId) : null,
    }));
  }

  isBootstrapped(): boolean {
    return (
      this.keyProviderRegistry.getActiveProvider().isReady() ||
      this.identityBackendRegistry.getActiveBackend().isReady()
    );
  }

  private getRecordCount(repositoryId: string): number {
    switch (repositoryId) {
      case 'repo-key-pairs':
        return this.repositories.keyPairs.count();
      case 'repo-addresses':
        return this.repositories.addresses.count();
      case 'repo-signatures':
        return this.repositories.signatures.count();
      case 'repo-identities':
        return this.repositories.identities.count();
      case 'repo-policies':
        return this.repositories.policies.count();
      case 'repo-trust-records':
        return this.repositories.trustRecords.count();
      default:
        return 0;
    }
  }
}
